import { useCallback, useMemo, useState } from "react";

import { addJWTInConfig } from "../../components/apiHooks/helpers";
import useApiRequestPost from "../../components/apiHooks/useApiRequestPost";
import { useConfigActions, useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { addDatadogAction, buildLocationContext } from "../../services/datadog";
import { recordPrint } from "../../services/printHistory";
import useGetSession from "../useGetSession/useGetSession";
import usePostPrint from "../usePostPrint/usePostPrint";
import { ICashQRResponse } from "./types";

const HOST: string = process.env.CLOUD_RUN_GATEWAY_HOST ?? "";
const END_POINT: string = process.env.API_CASH_QR ?? "";

const url: string = `${HOST}${END_POINT}`;

const QR_REGEX = /<qr[^>]*>(.*?)<\/qr>/;

export function parseQRContent(body: string): string | null {
	const match = body.match(QR_REGEX);

	return match ? match[1] : null;
}

const usePostCashQR = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const {
		state: { user },
	} = useConfigContext((state) => state);
	const { state: location } = useConfigContext((s) => s.location);
	const { state: stall } = useConfigContext((s) => s.config.stall);
	const configActions = useConfigActions();
	const { postData: refreshSession } = useGetSession({ autoFetch: false });
	const { submitPrint } = usePostPrint();

	const payload = useMemo(
		() => ({
			url,
			triggerOnMount: false,
			axiosConfig: addJWTInConfig({ timeout: 30000 }, user?.session_id?.toString() ?? ""),
			body: undefined,
			swrConfig: {
				revalidateOnFocus: false,
				revalidateOnReconnect: false,
				revalidateOnMount: false,
				revalidateIfStale: false,
				dedupingInterval: 0,
				refreshInterval: 0,
			},
		}),
		[user?.session_id]
	);

	const { postData } = useApiRequestPost(payload);

	const submitCashQR = useCallback(
		async (paymentReference: string, amountCents = 0): Promise<string | null> => {
			setIsLoading(true);
			setError(null);

			if (!paymentReference) {
				setError("Missing payment reference");
				setIsLoading(false);

				return null;
			}

			const submitStart = performance.now();

			try {
				let result;

				try {
					result = await postData({ payment_reference: paymentReference });
				} catch (err: any) {
					// /v1/sok/payment/qr enforces JWT auth since FOOD-2812. Token
					// freshness normally comes from the checkout-time session refresh
					// (useSubmitCheckout), but a lapsed token here would fail the sale —
					// refresh the session and retry once, mirroring the checkout pattern.
					if (err?.response?.status !== 401) {
						throw err;
					}

					let sessionResp;

					try {
						sessionResp = await refreshSession();
					} catch {
						// Surface the root-cause 401, not the secondary refresh failure —
						// telemetry below reports err.response.status.
						throw err;
					}

					const freshToken = sessionResp?.data?.token;

					if (!freshToken) {
						throw err;
					}

					configActions.SET_SESSION({ session_id: freshToken });
					addDatadogAction("cashqr_session_refreshed", {
						...buildLocationContext(location, stall),
						paymentReference,
					});

					// Pass the fresh token via overrideConfig — SET_SESSION's render
					// hasn't propagated to the payload useMemo inside this callback.
					result = await postData(
						{ payment_reference: paymentReference },
						addJWTInConfig({ timeout: 30000 }, freshToken)
					);
				}

				const cashQRData = result?.data as ICashQRResponse | undefined;

				if (!cashQRData?.body) {
					throw new Error("Invalid response from cash QR endpoint");
				}

				const qrContent = parseQRContent(cashQRData.body);

				// Cash QR posted = cashier-side confirmation event. Mutually
				// exclusive with cashqr_payment_timeout (catch branch below).
				addDatadogAction("cashqr_payment_confirmed", {
					...buildLocationContext(location, stall),
					paymentReference,
					durationMs: performance.now() - submitStart,
				});

				// Receipt was too small — bump font size and scale embedded QR.
				const enlargedBody = cashQRData.body.replace(
					/<qr([^>]*)\bsize=['"](\d+)['"]/i,
					(_m, attrs, size) => `<qr${attrs}size='${Math.round(parseInt(size, 10) * 1.5)}'`
				);

				// Header: stall / outlet / timestamp / short ref. Blank fields filtered
				// so missing outletid doesn't print an empty line.
				const header =
					[
						stall?.name,
						location?.outletid ? `Outlet: ${location.outletid}` : null,
						new Date().toLocaleString("en-SG", { timeZone: "Asia/Singapore" }),
						`Ref: ${paymentReference.substring(0, 16)}`,
						"--------------------------------",
					]
						.filter(Boolean)
						.join("\n") + "\n";

				// Printer driver only accepts { body, open_drawer, line_spacing }
				// — any extra top-level field (FOOD-2432 added font_size: 2) makes
				// it fall back to QR-only rendering, suppressing all body text.
				const printPayload = {
					...cashQRData,
					body: header + enlargedBody,
				};

				const printStubs = [JSON.stringify(printPayload)];

				try {
					await submitPrint(printStubs);
					recordPrint({ amountCents, type: "cashQR", payload: printStubs });
				} catch {
					// Print failure is non-blocking — QR screen still shows
				}

				setIsLoading(false);

				return qrContent;
			} catch (err: any) {
				const beMessage =
					err?.response?.data?.message || err?.response?.data?.error || err?.response?.statusText;
				const message = beMessage
					? `Cash QR request failed (${err.response.status}): ${beMessage}`
					: err instanceof Error
						? err.message
						: "Cash QR request failed";

				console.error("🔍 usePostCashQR error:", err?.response?.data ?? err);
				addDatadogAction("cashqr_payment_timeout", {
					...buildLocationContext(location, stall),
					paymentReference,
					durationMs: performance.now() - submitStart,
					// HTTP status distinguishes auth (401) / throttle (429) failures
					// from real timeouts once the FOOD-2812/2815/2816 gateway
					// protections are live. Undefined for network-level errors.
					status: err?.response?.status,
				});
				setError(message);
				setIsLoading(false);

				return null;
			}
		},
		[postData, refreshSession, configActions, submitPrint, location, stall]
	);

	return { submitCashQR, isLoading, error };
};

export default usePostCashQR;
